/**
 * Shared vocabulary and rules for Identity & Vision sharing (issue #292
 * slice 1).
 *
 * TWO surfaces use this and they serve opposite people: the player's own
 * control on the Identity & Vision tab (`IdentitySharingCard.jsx`) and the
 * coach's read-only panel on the portal (`PlayerIdentityPanel.jsx`). The
 * sentence that tells a player who can currently see what is safety copy,
 * so it is BUILT here, once, from the API's own answer — not typed into a
 * component where a third surface can ship a softer version of it.
 *
 * ============================================================
 * THE STATE MUST BE READABLE WITHOUT OPENING ANYTHING
 * ============================================================
 * #292 work item 3, and it is a hard requirement rather than a preference:
 * *"a control on the Identity & Vision tab that states in plain words who
 * can currently see what — the state must be readable without opening a
 * dialog"*. `describeVisibility` returns that sentence, fully formed, for a
 * component to render inline. A privacy control whose current state you
 * have to click to discover is a control people misjudge, and the thing
 * being misjudged here is who is reading their own words.
 *
 * ============================================================
 * IT NAMES THE COACHES, AND IT NAMES THEM FROM THE SERVER
 * ============================================================
 * The names come from `readers` on the API payload, resolved server-side
 * from the same ACTIVE `coach_links` predicate that gates the coach's read
 * (`api/src/routes/identitySharing.ts`). A client that assembled its own
 * coach list would be a second implementation of "who is my 1:1 coach",
 * free to disagree with the one that actually decides — and an invited
 * coach shown as a current reader would be a false statement in the one
 * place a player is deciding what to disclose.
 *
 * Naming them is NOT the per-named-individual control #292 rejected. There
 * is no per-coach switch anywhere in this feature; the grant is by
 * relationship. This is a statement of fact about who that relationship
 * currently resolves to.
 *
 * ============================================================
 * NO SENTENCE HERE MAY PROMISE MORE THAN THE SYSTEM DELIVERS
 * ============================================================
 * #205's honesty rule, the same one that forced `playerHealth.js`'s
 * `SHARING_DETAIL` to name the admin-takeover path. Turning a section off
 * hides it from the coach's next request — that is real and immediate — but
 * it does not erase what was written, does not un-remember what a coach
 * already read, and does not put the row beyond an operator with database
 * access (or #223's nightly backup). `SHARING_LIMIT` says so.
 */

/**
 * The three sections that can be shared, in the order both surfaces render
 * them, with the labels the player already sees on the tab.
 *
 * `mental_performance` is NOT here and must not be added. #214 moved that
 * section off this tab and onto Reflections; the API refuses it, and the
 * database cannot store a grant naming it. See
 * `api/src/lib/identitySections.ts`, which is the authority this mirrors.
 */
export const SHAREABLE_SECTIONS = [
  {
    key: "vision",
    label: "Player Vision",
    /** What the player is actually handing over, in their own terms. */
    hint: "Why you play, your ambitions, and the goals you've set yourself.",
  },
  {
    key: "strengths_weaknesses",
    label: "Strengths & Weaknesses",
    hint: "What you're confident in, what you're working on, and the situations you find hard.",
  },
  {
    key: "game_identity",
    label: "Game Identity",
    hint: "The kind of cricketer you are and the role you play in a side.",
  },
];

export const SECTION_KEYS = SHAREABLE_SECTIONS.map((s) => s.key);

/** Every section off — what a player who has never touched this shares. */
export const NOTHING_SHARED = { vision: false, strengths_weaknesses: false, game_identity: false };

export const SHARING_HEADING = "Who can see this";

/**
 * The read-only promise, told to the player where they decide. #292:
 * *"read-only for the coach, always — no coach edit path into a player's
 * own words, and no coach comment inside the section"*. It is a real
 * property of the API (there is no coach write route to write), so it is
 * safe to state plainly.
 */
export const READ_ONLY_NOTICE =
  "Your linked coaches can only read what you share. They can't edit your words, and they can't reply inside a section.";

/**
 * The honest limit. Not a disclaimer bolted on — the specific things a
 * player might otherwise assume, said in the order they'd assume them.
 */
export const SHARING_LIMIT =
  "Turning a section off hides it from your linked coaches straight away. It doesn't delete what you wrote, and it " +
  "can't un-read anything a coach has already seen.";

/** Told to the coach, on their panel, next to the words themselves. */
export const COACH_OWN_WORDS_NOTICE =
  "This is written by the player, in their own words, and shared with you because they chose to. You can read it; you can't change it.";

/**
 * What the coach's panel says when the player has shared nothing readable.
 *
 * IT MUST NOT HINT THAT THERE IS SOMETHING TO SEE. The API cannot tell a
 * coach whether a section exists and is withheld, or was never written —
 * that indistinguishability is the whole design — so this copy must not
 * reintroduce the distinction the payload was shaped to remove. "Nothing
 * shared with you" is the honest phrasing; "the player has chosen not to
 * share" is not, because it asserts a choice this screen cannot know was
 * made.
 */
export const COACH_NOTHING_SHARED =
  "Nothing to show here. Identity & Vision is the player's own writing, and it only appears if they choose to share it.";

/** "A", "A and B", "A, B and C" — an Oxford-comma-free list of names. */
export function joinNames(names) {
  const clean = (names || []).filter((n) => typeof n === "string" && n.trim() !== "");
  if (clean.length === 0) return "";
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(", ")} and ${clean[clean.length - 1]}`;
}

function labelsFor(keys) {
  return SHAREABLE_SECTIONS.filter((s) => keys.includes(s.key)).map((s) => s.label);
}

/**
 * THE sentence. Given the API's answer for one audience, say in plain words
 * who can currently see what.
 *
 * Four states, and each one is a different sentence rather than a
 * template with holes, because the interesting differences are not
 * quantities:
 *
 *   1. NO LINKED COACH AND NOTHING SHARED — the resting state of a new
 *      account. "Nobody can see it."
 *   2. NO LINKED COACH BUT SECTIONS ARE ON — the state a player is most
 *      likely to misread, and the reason this function exists. Their
 *      switches say "shared" and nobody is reading, but the moment they
 *      accept a coach invite those sections go live with no further
 *      gesture. Saying so is the difference between a control and a trap.
 *   3. A LINKED COACH, NOTHING SHARED — name them, and say plainly that
 *      they cannot see any of it. A coach being linked is not a coach
 *      reading.
 *   4. A LINKED COACH AND SECTIONS ON — name them, name what they can see,
 *      and name what they cannot. The player is entitled to the full
 *      picture; only the COACH's payload hides the sections that are off.
 */
export function describeVisibility({ readers, sections } = {}) {
  const names = joinNames(readers);
  const on = SECTION_KEYS.filter((k) => sections && sections[k]);
  const off = SECTION_KEYS.filter((k) => !(sections && sections[k]));
  const onLabels = joinNames(labelsFor(on));
  const offLabels = joinNames(labelsFor(off));

  if (!names) {
    if (on.length === 0) {
      return "Nobody can see your Identity & Vision. You don't have a linked coach right now, and nothing is shared.";
    }
    return `Nobody can see your Identity & Vision yet — you don't have a linked coach. ${onLabels} ${
      on.length === 1 ? "is" : "are"
    } switched on, so as soon as you accept a coach, they'll be able to read ${on.length === 1 ? "it" : "them"}.`;
  }

  if (on.length === 0) {
    return `${names} can see none of your Identity & Vision. Nothing is shared.`;
  }

  if (off.length === 0) {
    return `${names} can see all of your Identity & Vision: ${onLabels}.`;
  }

  return `${names} can see ${onLabels}. ${offLabels} ${off.length === 1 ? "is" : "are"} not shared.`;
}

/**
 * Pull the 1:1-coach audience out of the API's payload.
 *
 * Returns a stable shape even when the payload is missing or malformed, and
 * `NOTHING_SHARED` is the right failure value: a client that cannot read
 * the state must not render switches that say "on". The card treats a load
 * failure as a failure and says so (#271) rather than trusting this
 * default, but nothing downstream should be able to turn a broken response
 * into an over-statement of what is shared.
 */
export function readOneToOneAudience(payload) {
  const audiences = payload && Array.isArray(payload.audiences) ? payload.audiences : [];
  const found = audiences.find((a) => a && a.audience === "one_to_one_coach");
  const sections = { ...NOTHING_SHARED };
  if (found && found.sections) {
    for (const key of SECTION_KEYS) sections[key] = found.sections[key] === true;
  }
  const readers =
    found && Array.isArray(found.readers) ? found.readers.filter((n) => typeof n === "string") : [];
  return { sections, readers };
}

/** The body `PUT /players/me/identity/visibility` requires — all three keys. */
export function toVisibilityPayload(sections) {
  const body = {};
  for (const key of SECTION_KEYS) body[key] = (sections && sections[key]) === true;
  return { audience: "one_to_one_coach", sections: body };
}

/**
 * The sections present in a coach's payload, in render order.
 *
 * A section the payload omits is simply not here, and the panel renders no
 * trace of it — no heading, no "not shared", no greyed-out row. That is the
 * browser half of the API's non-oracle rule: the response cannot tell a
 * coach whether an absent section was withheld or never written, and a
 * placeholder in the UI would hand back exactly that distinction.
 */
export function presentSections(payload) {
  const sections = payload && payload.sections ? payload.sections : {};
  return SHAREABLE_SECTIONS.filter((s) => sections[s.key] && typeof sections[s.key] === "object").map((s) => ({
    ...s,
    content: sections[s.key],
  }));
}

/**
 * One schemaless section flattened into label/value pairs a panel can
 * render, dropping every empty answer.
 *
 * The sections are free-form objects (the API stores whatever the form
 * sends), so this cannot name fields. Keys become sentence-case labels;
 * arrays become comma lists; blanks disappear. Dropping blanks matters for
 * the same reason the API drops empty sections: a rendered empty row is a
 * statement that the player was asked something and did not answer, and the
 * coach has no business inferring that from a screen.
 */
export function toDisplayRows(content) {
  if (!content || typeof content !== "object" || Array.isArray(content)) return [];
  return Object.entries(content)
    .map(([key, value]) => {
      const items = displayItems(value);
      if (items.length === 0) return null;
      // `isList` follows the SHAPE of the stored answer, not its length: a
      // list-valued field (`top_ambitions`, `preferred_formats`) stays a
      // list even when the player filled in only one -- so it renders as a
      // chip, not silently reformatted into a sentence -- while a scalar
      // that happens to contain a comma is never mistaken for a list. This
      // is what #460 replaces: `displayAnswer` joined arrays into a comma
      // run-on that read as a paragraph, so a coach could not scan "Top
      // ambitions" as three separate goals.
      return {
        key,
        label: humaniseKey(key),
        value: items.join(", "),
        items,
        isList: Array.isArray(value),
      };
    })
    .filter(Boolean);
}

export function humaniseKey(key) {
  const words = String(key).replace(/_/g, " ").trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A single non-list answer, or `null` when there is nothing to display. */
function scalarAnswer(value) {
  if (typeof value === "string") return value.trim() === "" ? null : value.trim();
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : null;
  return null;
}

/**
 * A field's answer as a list of non-empty display strings. A scalar becomes
 * a one-item list; a blank, or an array that is all blanks (the form's
 * untouched `top_ambitions`), becomes `[]`. This is what lets a list-valued
 * field render as separate items -- a list or chips -- instead of the comma
 * run-on `displayAnswer` produces (#460).
 */
export function displayItems(value) {
  if (Array.isArray(value)) return value.map(scalarAnswer).filter((v) => v !== null);
  const only = scalarAnswer(value);
  return only === null ? [] : [only];
}

/** A displayable answer, or `null` when there is nothing to display. */
export function displayAnswer(value) {
  if (Array.isArray(value)) {
    const items = value.map(displayAnswer).filter((v) => v !== null);
    return items.length === 0 ? null : items.join(", ");
  }
  return scalarAnswer(value);
}
